import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Mail, 
  Loader2, 
  CheckCircle2,
  Clock,
  AlertCircle,
  RefreshCw,
  Send,
  FileText,
  Settings,
  Play,
  Shield
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSurveyLogPage = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [summary, setSummary] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const { token } = useAuth();

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logRes, jobRes] = await Promise.all([
        axios.get(`${API}/admin/surveys/sent-log`, {
          params: { status: statusFilter, limit: 200 },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/surveys/job-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSurveys(logRes.data.surveys || []);
      setSummary(logRes.data.summary);
      setJobStatus(jobRes.data);
    } catch (error) {
      console.error('Failed to load survey log:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(parseISO(isoString), "d. MMM yyyy HH:mm", { locale: da });
    } catch (e) {
      return isoString;
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(parseISO(isoString), "d. MMM", { locale: da });
    } catch (e) {
      return isoString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return (
          <Badge className="bg-blue-100 text-blue-700">
            <Send className="h-3 w-3 mr-1" />
            Sendt
          </Badge>
        );
      case 'submitted':
        return (
          <Badge className="bg-emerald-100 text-emerald-700">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Besvaret
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <Clock className="h-3 w-3 mr-1" />
            Afventer
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading && !surveys.length) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
            <Mail className="h-6 w-6 text-slate-700" />
            Survey Udsendelseslog
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Oversigt over survey-emails sendt til kunder
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/survey-duplicates')}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <Shield className="h-4 w-4 mr-2" />
            Spam-kontrol
          </Button>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Opdater
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total surveys</p>
                  <p className="text-2xl font-bold text-slate-900">{summary.total}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-full">
                  <FileText className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Emails sendt</p>
                  <p className="text-2xl font-bold text-blue-700">{summary.emails_sent}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Send className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Afventer</p>
                  <p className="text-2xl font-bold text-amber-700">{summary.pending}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Besvaret</p>
                  <p className="text-2xl font-bold text-emerald-700">{summary.submitted}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-full">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Job Status */}
      {jobStatus && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Survey Email Job Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-slate-500">Auto-send:</span>{' '}
                <Badge className={jobStatus.auto_send_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {jobStatus.auto_send_enabled ? 'Aktiveret' : 'Deaktiveret'}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500">Email API:</span>{' '}
                <Badge className={jobStatus.email_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {jobStatus.email_configured ? 'Konfigureret' : 'MANGLER'}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500">Klar til afsendelse:</span>{' '}
                <span className="font-semibold">{jobStatus.ready_to_send}</span>
              </div>
              <div>
                <span className="text-slate-500">Kører:</span>{' '}
                <span className="text-slate-700">{jobStatus.job_schedule}</span>
              </div>
              {jobStatus.last_job_runs && jobStatus.last_job_runs.length > 0 && (
                <div>
                  <span className="text-slate-500">Sidste kørsel:</span>{' '}
                  <span className="text-slate-700">
                    {formatDateTime(jobStatus.last_job_runs[0].timestamp)}
                    {' '}({jobStatus.last_job_runs[0].emails_sent || jobStatus.last_job_runs[0].sent || 0} sendt)
                  </span>
                </div>
              )}
              {jobStatus.last_job_runs && jobStatus.last_job_runs.length === 0 && (
                <div className="text-amber-600">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  Ingen job-kørsler fundet
                </div>
              )}
            </div>
            
            {/* Warning if email not configured */}
            {!jobStatus.email_configured && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                <strong>RESEND_API_KEY mangler!</strong> Emails kan ikke sendes før API-nøglen er konfigureret.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter and Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Survey Log</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Filtrer efter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="sent">Sendt (afventer svar)</SelectItem>
                <SelectItem value="pending">Afventer afsendelse</SelectItem>
                <SelectItem value="submitted">Besvaret</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Konsulent</TableHead>
                  <TableHead>Besøgsdato</TableHead>
                  <TableHead>Email sendt</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead>Årsag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {surveys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                      Ingen surveys fundet med det valgte filter
                    </TableCell>
                  </TableRow>
                ) : (
                  surveys.map((survey) => (
                    <TableRow key={survey.survey_id} data-testid={`survey-row-${survey.booking_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{survey.customer_name}</p>
                          <p className="text-xs text-slate-500">#{survey.booking_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {survey.customer_email || '-'}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {survey.consultant_name}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(survey.scheduled_start)}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {survey.email_sent_at ? formatDateTime(survey.email_sent_at) : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(survey.status)}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {survey.pending_reason || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSurveyLogPage;
